<template>
    <tr class="domain-row" :class="rowClasses" @click="goToSettings">
        <!-- DOMAIN -->
        <td class="px-6 py-4 min-w-0">
            <div
                class="domain-cell flex items-center gap-2 min-w-0"
                :class="domainCellClass"
            >
                <div class="domain-toggle">
                    <button
                        v-if="hasChildren"
                        type="button"
                        class="toggle-button"
                        @click.stop="emitToggle"
                    >
                        <ChevronUpIcon
                            v-if="isExpanded"
                            class="h-4 w-4 text-gray-500"
                        />
                        <ChevronDownIcon v-else class="h-4 w-4 text-gray-500" />
                    </button>
                </div>
                <div
                    class="h-2 w-2 rounded-full"
                    :class="domainStatusDotClass"
                ></div>
                <span
                    class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 min-w-0 truncate"
                    >{{ domain.domain }}</span
                >
                <span v-if="showSubdomainsCount" class="subdomain-count">
                    {{ subdomainsCountLabel }}
                </span>
            </div>
        </td>

        <!-- SLOT TYPE -->
        <!--        <td class="px-6 py-4 whitespace-nowrap min-w-0">-->
        <!--            <span :class="slotTypeLabel !== '-' ? slotBadgeClass : ''">-->
        <!--                {{ slotTypeLabel }}-->
        <!--            </span>-->
        <!--        </td>-->

        <!-- STATUS -->
        <td class="px-6 py-4 whitespace-nowrap min-w-0">
            <span v-if="showStatus === false" class="policy-badge policy-none">
                -
            </span>
            <span v-else :class="statusBadgeClass">
                {{ statusLabel }}
            </span>
        </td>

        <!-- CREATED -->
        <td
            v-if="showParkedWhileCreationNote === false"
            class="px-6 py-4 whitespace-nowrap min-w-0"
        >
            <span
                class="cell-text"
                :class="
                    createdAtLabel === '-' ? 'policy-badge policy-none' : ''
                "
            >
                {{ createdAtLabel }}
            </span>
        </td>

        <!-- DMARC POLICY -->
        <td
            class="px-6 py-4 min-w-0"
            :colspan="showParkedWhileCreationNote === true ? 2 : 1"
        >
            <span
                v-if="showParkedWhileCreationNote === true"
                class="report-domain-note"
            >
                {{ $t("vue.parked_while_creation_note") }}
            </span>
            <span
                v-else
                :class="
                    policyLabel !== '-'
                        ? policyBadgeClass
                        : 'policy-badge policy-none'
                "
            >
                {{ policyLabel }}
            </span>
        </td>

        <!-- ACTIONS -->
        <td class="px-3 py-4 whitespace-nowrap text-center" @click.stop>
            <button
                v-if="showCreateFromReportsButton === true && !demoMode"
                type="button"
                class="add-from-reports-button"
                :disabled="creatingFromReports"
                @click="onCreateDomainParkedFromReports"
            >
                <PlusCircleIcon class="h-4 w-4" />
                <span>{{ $t("vue.add_domain") }}</span>
            </button>
            <KebabMenu
                v-else-if="showActions === true"
                :use-transition="false"
                pointer-all
            >
                <template #default="{ close }">
                    <button
                        v-if="!domain.parked"
                        class="menu-item"
                        role="menuitem"
                        @click="goToReports"
                    >
                        {{ $t("vue.see_reports") }}
                    </button>
                    <button
                        class="menu-item"
                        role="menuitem"
                        @click="goToSettings"
                    >
                        {{ $t("vue.settings") }}
                    </button>
                    <button
                        v-if="!domain.parked"
                        class="menu-item"
                        role="menuitem"
                        @click="goToDmarcManagement"
                    >
                        {{ $t("vue.dmarc_management") }}
                    </button>
                    <button
                        v-if="!domain.parked && !domain.active && !demoMode"
                        class="menu-item"
                        role="menuitem"
                        @click="goToDomainWizard"
                    >
                        {{ $t("vue.domain_wizard") }}
                    </button>
                    <button
                        class="menu-item warning"
                        :class="{ disabled: !canManipulateDomains }"
                        :aria-disabled="(!canManipulateDomains).toString()"
                        :title="domainManipulationDisabledMessage"
                        role="menuitem"
                        @click="
                            () => {
                                close();
                                onParkDomainToggle();
                            }
                        "
                    >
                        {{
                            domain.parked
                                ? $t("vue.unpark_domain")
                                : $t("vue.park_domain")
                        }}
                    </button>
                    <button
                        class="menu-item danger"
                        :class="{ disabled: !canManipulateDomains }"
                        :aria-disabled="(!canManipulateDomains).toString()"
                        :title="domainManipulationDisabledMessage"
                        role="menuitem"
                        @click="
                            () => {
                                close();
                                onDelete();
                            }
                        "
                    >
                        {{ $t("vue.delete_domain") }}
                    </button>
                </template>
            </KebabMenu>
        </td>
    </tr>
</template>

<script lang="ts" setup>
import type { PropType } from "vue";
import { computed } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import route from "ziggy-js";
import { format } from "date-fns";
import type IDomain from "@/types/Domains/IDomain";
import { useTranslations } from "@/composables/useTranslations";
import useDomainManipulationPermissions from "@/composables/UseDomainManipulationPermissions";
import { PlusCircleIcon } from "@heroicons/vue/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import {
    getDomainStatusBadgeClass,
    getDomainStatusDotClass,
    getDomainStatusTranslationKey,
} from "@/utils/domainStatus";

const { t } = useTranslations();
const {
    canManipulateDomains,
    domainManipulationDisabledMessage,
    ensureCanManipulateDomains,
} = useDomainManipulationPermissions();

const emit = defineEmits([
    "remove",
    "validate",
    "refreshDomains",
    "toggle",
    "togglePark",
    "createDomainParkedFromReports",
]);

const demoMode = computed(() => usePage<IRhizomPageProps>().props.demoMode);

const props = defineProps({
    domain: { type: Object as PropType<IDomain>, required: true },
    validationLoading: { type: Boolean, default: false },
    dnsChecking: { type: Boolean, default: false },
    hasChildren: { type: Boolean, default: false },
    isExpanded: { type: Boolean, default: false },
    isSubdomainRow: { type: Boolean, default: false },
    hideActions: { type: Boolean, default: false },
    disableClick: { type: Boolean, default: false },
    subdomainsCount: { type: Number, default: 0 },
    creatingFromReports: { type: Boolean, default: false },
});

const isExternal = computed(() => props.domain.isExternal ?? false);
const isOtherGroup = computed(() => props.domain.isOtherGroup ?? false);
const isSubdomain = computed(
    () => props.isSubdomainRow || props.domain.isSubdomain === true,
);

const isClickable = computed(() => !props.disableClick && !isExternal.value);

const rowClasses = computed(() => ({
    "domain-row--subdomain": isSubdomain.value,
    "domain-row--clickable": isClickable.value,
}));

const domainCellClass = computed(() =>
    isSubdomain.value ? "domain-cell--subdomain" : "",
);

const domainStatusDotClass = computed(() => {
    if (isOtherGroup.value) {
        return "invisible";
    }

    return getDomainStatusDotClass(props.domain);
});

const emitToggle = () => {
    emit("toggle");
};

const subdomainsCount = computed(
    () => props.subdomainsCount ?? props.domain.subdomainsCount ?? 0,
);

const showSubdomainsCount = computed(
    () => !isSubdomain.value && subdomainsCount.value > 0,
);

const subdomainsCountLabel = computed(() =>
    t("vue.subdomains_detected", { count: subdomainsCount.value }),
);

const slotType = computed(() => {
    if (isOtherGroup.value) return null;
    if (props.domain.slotType) {
        return props.domain.slotType;
    }

    return isSubdomain.value ? t("vue.billable") : t("vue.non_billable");
});

const slotTypeLabel = computed(() => {
    if (slotType.value === null) return "-";
    return slotType.value === "FREE"
        ? t("vue.non_billable")
        : t("vue.billable");
});

const slotBadgeClass = computed(() => {
    const base = "policy-badge";
    if (slotType.value === null) return `${base} policy-none`;

    return slotType.value === "FREE"
        ? `${base} slot-free`
        : `${base} slot-paid`;
});

const createdAtLabel = computed(() => {
    if (isOtherGroup.value) return "-";
    if (!props.domain.created_at) return "-";

    const parsed = new Date(props.domain.created_at as string);
    if (Number.isNaN(parsed.getTime())) return "-";

    return format(parsed, "d. L. yyyy");
});

const isParkedWhileCreation = computed(
    () =>
        props.domain.parked_while_creation === true ||
        props.domain.parkedWhileCreation === true,
);
const showStatus = computed(
    () =>
        isOtherGroup.value === false &&
        (isExternal.value === false || isParkedWhileCreation.value === true),
);
const showParkedWhileCreationNote = computed(
    () => isParkedWhileCreation.value === true,
);
const showCreateFromReportsButton = computed(
    () => showParkedWhileCreationNote.value === true,
);

const statusBadgeClass = computed(() => {
    return getDomainStatusBadgeClass(props.domain);
});

const statusLabel = computed(() => {
    return t(getDomainStatusTranslationKey(props.domain));
});

const DMARC_POLICY_RE = /(?:^|;\s*)p\s*=\s*(none|quarantine|reject)\b/i;

// A domain only carries a policy of its own when it has its own DMARC record.
// Subdomains without one inherit from their parent and must render as "-".
const hasOwnDmarcRecord = computed(
    () =>
        typeof props.domain.dmarcRecord === "string" &&
        props.domain.dmarcRecord.trim() !== "",
);

// Parse the policy straight from the record so trailing artifacts (quotes,
// whitespace, extra tags) never break recognition of reject/quarantine.
const parsedPolicy = computed<"none" | "quarantine" | "reject">(() => {
    const match = (props.domain.dmarcRecord ?? "").match(DMARC_POLICY_RE);
    const p = (match?.[1] ?? "none").toLowerCase();
    if (p === "reject") return "reject";
    if (p === "quarantine") return "quarantine";
    return "none";
});

const policyLabel = computed(() => {
    if (isOtherGroup.value || isExternal.value) return "-";
    if (!hasOwnDmarcRecord.value) {
        // Inherited subdomains have no own policy to show.
        return isSubdomain.value ? "-" : "none";
    }
    return parsedPolicy.value;
});

const policyBadgeClass = computed(() => {
    const base = "policy-badge";
    if (isOtherGroup.value || isExternal.value || !hasOwnDmarcRecord.value) {
        return `${base} policy-none`;
    }

    if (parsedPolicy.value === "reject") return `${base} policy-reject`;
    if (parsedPolicy.value === "quarantine") return `${base} policy-quarantine`;
    return `${base} policy-none`;
});

const goToReports = () => {
    if (!isClickable.value) return;
    router.get(route("reports.show", { domain: props.domain.domain }));
};

const goToDomainWizard = () => {
    if (!isClickable.value) return;
    router.get(route("domainGuide.index", { domain: props.domain.domain }));
};

const goToDmarcManagement = () => {
    if (!isClickable.value) return;
    router.get(
        route("reports.dmarc-management.show", { domain: props.domain.domain }),
    );
};

const goToSettings = () => {
    if (!isClickable.value) return;
    const url =
        route("reports.show", { domain: props.domain.domain }) + "/settings";
    router.get(url);
};

const onDelete = () => {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    emit("remove", props.domain.domain);
};

const onParkDomainToggle = () => {
    if (!ensureCanManipulateDomains()) {
        return;
    }

    emit("togglePark", props.domain);
};

const onCreateDomainParkedFromReports = () => {
    emit("createDomainParkedFromReports", props.domain);
};

const showActions = computed(() => !props.hideActions && !isExternal.value);
</script>

<style>
.domain-row {
    transition: background-color 0.12s ease;
    height: 49.75px;
}

.domain-row > td {
    height: 49.75px;
    padding-top: 6px;
    padding-bottom: 6px;
    vertical-align: middle;
}

.domain-row:hover td {
    background: #fafafa;
}

.domain-row--clickable:hover td {
    cursor: pointer;
}

.domain-cell--subdomain {
    padding-left: 28px;
}

.domain-toggle {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
}

.toggle-button {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.12s ease;
}

.toggle-button:hover {
    background: #f3f4f6;
}

.subdomain-count {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: #6b7280;
    background: #f3f4f6;
}

.slot-paid {
    background: #eafaf0;
    color: #0a7d3a;
}

.slot-free {
    background: #f3f4f6;
    color: #6b7280;
}

.cell-text {
    color: #374151;
}

.report-domain-note {
    display: block;
    max-width: 520px;
    color: #374151;
    font-size: 12px;
    line-height: 16px;
    white-space: normal;
}

.add-from-reports-button {
    display: inline-flex;
    height: 32px;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border-radius: 6px;
    background: #088bea;
    padding: 0 12px;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    transition:
        background-color 0.12s ease,
        opacity 0.12s ease;
}

.add-from-reports-button:hover {
    background: #0476c9;
}

.add-from-reports-button:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}
</style>
